import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "../services/api";
import { fetchUsers } from "../services/userService";
import { param, useListUrlState, yesNo } from "./useUrlState";

const FILTERS = {
  search: param.string("", { debounce: 400 }),
  roleId: param.string(),
  isActive: yesNo(),
};

/**
 * The business's staff, with the list's paging and filter state.
 * `reload()` refetches the current page after a mutation.
 */
export default function useUsers() {
  const { t } = useTranslation();

  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState(null);
  // Page, filters and search live in the URL — see useUrlState.
  const list = useListUrlState(FILTERS);
  const { page, filters } = list;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { users: list, meta: pagination } = await fetchUsers({ page, filters });
      setUsers(list);
      setMeta(pagination);
    } catch (err) {
      setError(apiErrorMessage(err, t("staff.loadFailed")));
      setUsers([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, [page, filters, t]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    users,
    meta,
    page,
    setPage: list.setPage,
    filters,
    applyFilters: list.applyFilters,
    resetFilters: list.resetFilters,
    searchTerm: list.searchTerm,
    setSearchTerm: list.setSearchTerm,
    submitSearch: list.submitSearch,
    loading,
    error,
    reload: load,
  };
}
